import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Box, Button, Collapse, Divider, Flex, HStack, Icon, Input, Switch, Text, Textarea, useToast, VStack } from "@chakra-ui/react";
import { useNavigate, useBlocker } from "react-router-dom";
import { motion } from "framer-motion";
import {
  PiUsersThreeDuotone, PiCaretLeftBold, PiListChecksDuotone, PiSparkleDuotone, PiCheckBold, PiWarningCircleDuotone,
  PiEyeDuotone, PiLockDuotone, PiGlobeDuotone, PiUserCircleDuotone, PiEyeSlashDuotone,
  PiIdentificationCardDuotone, PiSealCheckDuotone, PiCheckCircleDuotone, PiShieldCheckDuotone, PiChatsDuotone,
  PiUserPlusDuotone, PiCalendarBlankDuotone, PiGavelDuotone
} from "react-icons/pi";
import { createVoterGroup } from "@lubbezposrednio-frontend/core";
import { GLASS, SCROLL_FOG, drgba, EASE } from "../../components/glassUi";

const MotionDiv = motion.div;
const NAME = "#10b981";

// ── Sekcja: ciemniejsze zaokrąglone tło + mini-nagłówek (jak w głosowaniu) ──
const Section: React.FC<{ icon: React.ElementType; color: string; title: string; children: React.ReactNode }> = ({ icon, color, title, children }) => (
  <Box bg="rgba(24,12,48,0.028)" borderRadius="20px" p={{ base: "18px", md: "22px" }}>
    <HStack spacing="10px" mb="16px"><Icon as={icon} boxSize="20px" color={color} /><Text fontSize="16px" fontWeight="800" color="#241536" letterSpacing="-0.01em">{title}</Text></HStack>
    <VStack align="stretch" spacing="16px">{children}</VStack>
  </Box>
);
const SubLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text fontSize="11.5px" fontWeight="800" letterSpacing="0.04em" textTransform="uppercase" color="#a29bb2" mb="10px">{children}</Text>
);

type Opt = { value: string; label: string; desc: string; icon: React.ElementType; color: string };
const OptionCard: React.FC<{ opt: Opt; active: boolean; onClick: () => void }> = ({ opt, active, onClick }) => (
  <Box as="button" type="button" onClick={onClick} textAlign="left" position="relative" p="14px 15px" borderRadius="15px"
    border="1.5px solid" borderColor={active ? opt.color : "rgba(24,12,48,0.1)"} bg={active ? drgba(opt.color, 0.08) : "rgba(255,255,255,0.5)"}
    transition={`all ${EASE}`} _hover={{ borderColor: opt.color, bg: drgba(opt.color, active ? 0.1 : 0.05), transform: "translateY(-1px)" }} _active={{ transform: "translateY(0)" }}>
    <HStack spacing="11px" align="flex-start">
      <Flex w="38px" h="38px" borderRadius="11px" align="center" justify="center" bg={active ? opt.color : drgba(opt.color, 0.12)} flexShrink={0} transition={`all ${EASE}`}>
        <Icon as={opt.icon} boxSize="20px" color={active ? "white" : opt.color} />
      </Flex>
      <Box pr="16px"><Text fontSize="14px" fontWeight="800" color="#241536" lineHeight="1.2">{opt.label}</Text><Text fontSize="12px" color="#8a8397" mt="3px" lineHeight="1.4">{opt.desc}</Text></Box>
    </HStack>
    {active && <Flex position="absolute" top="10px" right="10px" w="18px" h="18px" borderRadius="full" bg={opt.color} align="center" justify="center"><Icon as={PiCheckBold} boxSize="10px" color="white" /></Flex>}
  </Box>
);

const Segmented: React.FC<{ options: { value: string; label: string; icon: React.ElementType }[]; value: string; onChange: (v: string) => void; color: string }> = ({ options, value, onChange, color }) => (
  <Flex bg="rgba(24,12,48,0.045)" borderRadius="14px" p="5px" gap="4px">
    {options.map(o => {
      const active = o.value === value;
      return (
        <Flex key={o.value} as="button" type="button" flex="1" direction="column" align="center" gap="6px" py="11px" px="6px" borderRadius="10px"
          bg={active ? "white" : "transparent"} boxShadow={active ? "0 2px 8px -3px rgba(43,0,90,0.3)" : undefined} transition={`all ${EASE}`} onClick={() => onChange(o.value)}>
          <Icon as={o.icon} boxSize="19px" color={active ? color : "#9a94ab"} />
          <Text fontSize="12.5px" fontWeight="800" color={active ? "#241536" : "#7a748a"} textAlign="center" lineHeight="1.15">{o.label}</Text>
        </Flex>
      );
    })}
  </Flex>
);

const ToggleRow: React.FC<{ icon: React.ElementType; color: string; label: string; desc: string; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }> = ({ icon, color, label, desc, checked, onChange, disabled }) => (
  <Flex align="center" justify="space-between" gap="14px" opacity={disabled ? 0.6 : 1}>
    <HStack spacing="11px" minW={0}>
      <Flex w="34px" h="34px" borderRadius="10px" bg={drgba(color, 0.12)} align="center" justify="center" flexShrink={0}><Icon as={icon} boxSize="17px" color={color} /></Flex>
      <Box minW={0}><Text fontSize="14px" fontWeight="700" color="#3f3a4d">{label}</Text><Text fontSize="12.5px" color="#8a8397" lineHeight="1.4">{desc}</Text></Box>
    </HStack>
    <Switch size="lg" colorScheme="green" isChecked={checked} onChange={e => onChange(e.target.checked)} isDisabled={disabled} flexShrink={0} />
  </Flex>
);

export const CreateGroupViewV2: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [visibility, setVisibility] = useState<"private" | "public">("private");
  const [visibleOnProfile, setVisibleOnProfile] = useState(false);
  const [contentHidden, setContentHidden] = useState(false);
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [accountRequired, setAccountRequired] = useState(false);
  const [verificationRequired, setVerificationRequired] = useState(false);
  const [whoCanPost, setWhoCanPost] = useState<"all" | "verified" | "owner">("all");
  const [whoCanCreateVotings, setWhoCanCreateVotings] = useState<"owner" | "moderators" | "members">("owner");
  const [signupEnabled, setSignupEnabled] = useState(false);
  const [signupTitle, setSignupTitle] = useState("");
  const [signupDescription, setSignupDescription] = useState("");
  const [signupClosesAt, setSignupClosesAt] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const submittedRef = useRef(false); // synchronicznie wyłącza blocker przed nawigacją

  useEffect(() => { document.title = "Nowa grupa"; }, []);

  const titleRef = useRef<HTMLTextAreaElement>(null);
  const descRef = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => { const el = titleRef.current; if (el) { el.style.height = "auto"; el.style.height = `${el.scrollHeight}px`; } }, [name]);
  useLayoutEffect(() => { const el = descRef.current; if (el) { el.style.height = "auto"; el.style.height = `${el.scrollHeight}px`; } }, [description]);

  // weryfikacja implikuje konto
  const accountEffective = accountRequired || verificationRequired;

  // ── Walidacja ──
  const issues: { target: string; message: string }[] = [];
  const nm = name.trim();
  if (!nm) issues.push({ target: "cg-name", message: "Podaj nazwę grupy." });
  else if (nm.length < 3) issues.push({ target: "cg-name", message: "Nazwa musi mieć min. 3 znaki." });
  if (signupEnabled && !signupTitle.trim()) issues.push({ target: "cg-signup", message: "Zapisy włączone — podaj tytuł strony zapisów." });
  const canCreate = issues.length === 0;
  const isDirty = () => !submittedRef.current && !done && (!!name.trim() || !!description.trim() || signupEnabled || visibility !== "private" || visibleOnProfile || contentHidden || requiresApproval || accountEffective || whoCanPost !== "all" || whoCanCreateVotings !== "owner");

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => { if (!isDirty()) return; e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
    // eslint-disable-next-line
  });

  const blocker = useBlocker(({ currentLocation, nextLocation }) => isDirty() && currentLocation.pathname !== nextLocation.pathname);
  useEffect(() => {
    if (blocker.state === "blocked") {
      if (window.confirm("Masz niezapisaną treść tworzonej grupy. Na pewno opuścić stronę?")) blocker.proceed();
      else blocker.reset();
    }
    // eslint-disable-next-line
  }, [blocker.state]);

  const create = async () => {
    if (!canCreate) return;
    setSaving(true);
    try {
      const r = await createVoterGroup({
        name: nm,
        description: description.trim() || undefined,
        visibility,
        visible_on_profile: visibleOnProfile,
        content_hidden: contentHidden,
        requires_approval: requiresApproval,
        account_required: accountEffective,
        verification_required: verificationRequired,
        who_can_post: whoCanPost,
        who_can_create_votings: whoCanCreateVotings,
        signup_enabled: signupEnabled,
        signup_title: signupEnabled ? signupTitle.trim() || null : null,
        signup_description: signupEnabled ? signupDescription.trim() || null : null,
        signup_closes_at: signupEnabled && signupClosesAt ? `${signupClosesAt}T23:59:59` : null
      });
      submittedRef.current = true; // wyłącz blocker PRZED nawigacją (stan async by nie zdążył)
      setDone(true);
      toast({ title: "Grupa utworzona — możesz dodać członków", status: "success", isClosable: true });
      navigate(`/organizator/grupy/${r.data.voter_group.id}`);
    } catch {
      toast({ title: "Nie udało się utworzyć grupy. Spróbuj ponownie.", status: "error", isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  const gotoTarget = (t: string) => { const el = document.getElementById(t); el?.scrollIntoView({ behavior: "smooth", block: "center" }); (el?.querySelector?.("input, textarea") as HTMLElement | null)?.focus?.({ preventScroll: true }); };

  const reqItems = [
    { key: "cg-name", label: "Nazwa grupy" },
    ...(signupEnabled ? [{ key: "cg-signup", label: "Strona zapisów" }] : [])
  ].map(it => ({ ...it, msgs: issues.filter(i => i.target === it.key).map(i => i.message) }));
  const doneCount = reqItems.filter(i => i.msgs.length === 0).length;

  const rail = (
    <>
      <Box borderRadius="18px" p="16px" bg="rgba(255,255,255,0.55)" border="1px solid rgba(24,12,48,0.06)">
        <Flex align="center" justify="space-between" mb="12px">
          <HStack spacing="8px"><Icon as={canCreate ? PiSparkleDuotone : PiListChecksDuotone} boxSize="17px" color={canCreate ? "#10b981" : "#7b2ff7"} /><Text fontSize="12px" fontWeight="900" letterSpacing="0.04em" textTransform="uppercase" color={canCreate ? "#10b981" : "#6b6480"}>{canCreate ? "Gotowe" : "Do uzupełnienia"}</Text></HStack>
          <Text fontSize="12px" fontWeight="800" color={canCreate ? "#10b981" : "#a29bb2"}>{doneCount}/{reqItems.length}</Text>
        </Flex>
        <VStack align="stretch" spacing="8px">
          {reqItems.map(it => {
            const ok = it.msgs.length === 0;
            return (
              <Flex key={it.key} as="button" type="button" w="100%" textAlign="left" onClick={() => gotoTarget(it.key)} align="flex-start" gap="11px" p="10px 11px" borderRadius="13px" transition={`all ${EASE}`}
                bg={ok ? drgba("#10b981", 0.12) : "rgba(24,12,48,0.04)"} border="1px solid" borderColor={ok ? drgba("#10b981", 0.32) : "transparent"} _hover={{ transform: "translateX(2px)" }}>
                <Flex w="30px" h="30px" borderRadius="9px" align="center" justify="center" flexShrink={0} mt="1px" bg={ok ? "#10b981" : "rgba(24,12,48,0.09)"}>
                  {ok ? <Icon as={PiCheckBold} boxSize="15px" color="white" /> : <Icon as={PiUsersThreeDuotone} boxSize="16px" color="#a29bb2" />}
                </Flex>
                <Box flex="1" minW={0}>
                  <Text fontSize="13.5px" fontWeight={ok ? "800" : "700"} color={ok ? "#0f7a52" : "#241536"}>{it.label}</Text>
                  {!ok && <VStack align="stretch" spacing="4px" mt="5px">{it.msgs.map((m, i) => <HStack key={i} align="flex-start" spacing="6px"><Icon as={PiWarningCircleDuotone} boxSize="13px" color="#e08a00" mt="1px" flexShrink={0} /><Text fontSize="12px" color="#8a6d3b" lineHeight="1.4">{m}</Text></HStack>)}</VStack>}
                </Box>
              </Flex>
            );
          })}
        </VStack>
      </Box>
      <Divider my="20px" borderColor="rgba(24,12,48,0.08)" />
      <Box><Text fontSize="12px" fontWeight="900" letterSpacing="0.05em" textTransform="uppercase" color="#a29bb2" mb="8px">Podpowiedź</Text><Text fontSize="13px" color="#6b6480" lineHeight="1.55">Członków dodasz po utworzeniu — na stronie grupy (wyszukaj konta, wgraj CSV, albo udostępnij link zapisów).</Text></Box>
    </>
  );

  return (
    <Flex align="flex-start">
      <Box flex="1" minW={0}>
        <Box w="100%" maxW="1000px" mx="auto" px={{ base: "20px", md: "44px" }} py={{ base: "26px", md: "40px" }}>
          <MotionDiv initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}>
            <Box borderRadius="24px" p={{ base: "24px", md: "34px" }} position="relative" overflow="hidden" sx={GLASS}>
              <Box position="absolute" top="-90px" right="-70px" w="300px" h="300px" borderRadius="full" pointerEvents="none" sx={{ background: `radial-gradient(circle, ${drgba(NAME, 0.09)}, transparent 70%)` }} />

              <HStack spacing="9px" mb="16px" position="relative"><Icon as={PiUsersThreeDuotone} boxSize="18px" color={NAME} /><Text fontSize="12px" fontWeight="800" letterSpacing="0.05em" color="#8a8397" textTransform="uppercase">Nowa grupa</Text></HStack>

              <Box position="relative" id="cg-name">
                <Textarea ref={titleRef} value={name} onChange={e => setName(e.target.value)} onKeyDown={e => { if (e.key === "Enter") e.preventDefault(); }}
                  maxLength={120} rows={1} placeholder="Nazwa grupy…" variant="unstyled" w="100%" resize="none" overflow="hidden" minH="unset"
                  fontSize="clamp(27px, 4.4vw, 40px)" fontWeight="900" color="#0f0820" letterSpacing="-0.03em" lineHeight="1.14" px="8px" py="10px" mx="-8px" borderRadius="14px"
                  transition={`background ${EASE}`} _placeholder={{ color: "#c8c2d6", fontWeight: 900 }} _hover={{ bg: "rgba(24,12,48,0.03)" }} _focus={{ bg: drgba(NAME, 0.05) }} _focusVisible={{ outline: "none", boxShadow: "none" }} sx={{ caretColor: NAME }} />
                <Textarea ref={descRef} value={description} onChange={e => setDescription(e.target.value)} maxLength={2000} rows={1}
                  placeholder="Dodaj opis (opcjonalnie) — czym jest ta grupa…" variant="unstyled" w="100%" resize="none" overflow="hidden" minH="unset" mt="6px"
                  fontSize="clamp(15px, 1.9vw, 17px)" fontWeight="500" color="#4a4458" lineHeight="1.55" px="8px" py="6px" mx="-8px" borderRadius="12px"
                  transition={`background ${EASE}`} _placeholder={{ color: "#b8b2c6", fontWeight: 500 }} _hover={{ bg: "rgba(24,12,48,0.025)" }} _focus={{ bg: drgba(NAME, 0.04) }} _focusVisible={{ outline: "none", boxShadow: "none" }} sx={{ caretColor: NAME }} />
              </Box>

              {/* ── Widoczność grupy ── */}
              <Box mt={{ base: "26px", md: "34px" }}>
                <Section icon={PiEyeDuotone} color="#2563eb" title="Widoczność grupy">
                  <Box>
                    <SubLabel>Kto widzi grupę</SubLabel>
                    <Box display="grid" gridTemplateColumns={{ base: "1fr", sm: "1fr 1fr" }} gap="10px">
                      <OptionCard active={visibility === "private"} onClick={() => setVisibility("private")} opt={{ value: "private", label: "Prywatna", desc: "Widzą tylko członkowie; grupa nie pojawia się publicznie.", icon: PiLockDuotone, color: "#6b7280" }} />
                      <OptionCard active={visibility === "public"} onClick={() => setVisibility("public")} opt={{ value: "public", label: "Publiczna", desc: "Grupa i jej ściana są widoczne publicznie.", icon: PiGlobeDuotone, color: "#2563eb" }} />
                    </Box>
                  </Box>
                  <ToggleRow icon={PiUserCircleDuotone} color="#2563eb" label="Pokaż na moim profilu publicznym" desc="Grupa pojawi się na Twoim profilu @." checked={visibleOnProfile} onChange={setVisibleOnProfile} />
                  <ToggleRow icon={PiEyeSlashDuotone} color="#7b2ff7" label="Ukryj treść ściany" desc="Posty widoczne tylko dla członków, nawet gdy grupa publiczna." checked={contentHidden} onChange={setContentHidden} />
                </Section>
              </Box>

              {/* ── Członkostwo ── */}
              <Box mt={{ base: "14px", md: "16px" }}>
                <Section icon={PiIdentificationCardDuotone} color="#10b981" title="Członkostwo">
                  <ToggleRow icon={PiCheckCircleDuotone} color="#10b981" label="Dołączenie wymaga akceptacji" desc="Nowi członkowie czekają na Twoje zatwierdzenie." checked={requiresApproval} onChange={setRequiresApproval} />
                  <ToggleRow icon={PiUserCircleDuotone} color="#2563eb" label="Wymagaj konta" desc="Członkiem może być tylko zalogowany użytkownik." checked={accountEffective} onChange={setAccountRequired} disabled={verificationRequired} />
                  <ToggleRow icon={PiSealCheckDuotone} color="#0d9488" label="Wymagaj zweryfikowanej tożsamości" desc="mObywatel — implikuje konto." checked={verificationRequired} onChange={v => { setVerificationRequired(v); if (v) setAccountRequired(true); }} />
                </Section>
              </Box>

              {/* ── Uprawnienia ── */}
              <Box mt={{ base: "14px", md: "16px" }}>
                <Section icon={PiShieldCheckDuotone} color="#0d9488" title="Uprawnienia">
                  <Box>
                    <SubLabel>Kto może pisać na ścianie</SubLabel>
                    <Segmented color="#0d9488" value={whoCanPost} onChange={v => setWhoCanPost(v as typeof whoCanPost)} options={[
                      { value: "all", label: "Wszyscy", icon: PiChatsDuotone },
                      { value: "verified", label: "Zweryfikowani", icon: PiSealCheckDuotone },
                      { value: "owner", label: "Właściciel", icon: PiUserCircleDuotone }
                    ]} />
                  </Box>
                  <Box>
                    <SubLabel>Kto może tworzyć głosowania</SubLabel>
                    <Segmented color="#7b2ff7" value={whoCanCreateVotings} onChange={v => setWhoCanCreateVotings(v as typeof whoCanCreateVotings)} options={[
                      { value: "owner", label: "Właściciel", icon: PiUserCircleDuotone },
                      { value: "moderators", label: "Moderatorzy", icon: PiGavelDuotone },
                      { value: "members", label: "Członkowie", icon: PiUsersThreeDuotone }
                    ]} />
                  </Box>
                </Section>
              </Box>

              {/* ── Zapisy ── */}
              <Box mt={{ base: "14px", md: "16px" }} id="cg-signup">
                <Section icon={PiUserPlusDuotone} color="#7b2ff7" title="Publiczne zapisy">
                  <ToggleRow icon={PiUserPlusDuotone} color="#7b2ff7" label="Włącz stronę zapisów" desc="Powstanie publiczny link; ludzie zapisują się do grupy (z potwierdzeniem e-mail)." checked={signupEnabled} onChange={setSignupEnabled} />
                  <Collapse in={signupEnabled} animateOpacity unmountOnExit>
                    <VStack align="stretch" spacing="14px" pt="2px">
                      <Box>
                        <Text fontSize="14px" fontWeight="700" color="#3f3a4d" mb="8px">Tytuł strony zapisów</Text>
                        <Input value={signupTitle} onChange={e => setSignupTitle(e.target.value)} maxLength={120} placeholder="np. Dołącz do naszej wspólnoty" h="46px" borderRadius="12px" bg="rgba(255,255,255,0.72)" border="1.5px solid rgba(24,12,48,0.1)" fontWeight="600" _hover={{ borderColor: drgba("#7b2ff7", 0.4) }} _focusVisible={{ borderColor: "#7b2ff7", boxShadow: `0 0 0 3px ${drgba("#7b2ff7", 0.16)}` }} />
                      </Box>
                      <Box>
                        <Text fontSize="14px" fontWeight="700" color="#3f3a4d" mb="8px">Opis (opcjonalnie)</Text>
                        <Textarea value={signupDescription} onChange={e => setSignupDescription(e.target.value)} maxLength={1000} placeholder="Krótko, kogo zapraszasz…" borderRadius="12px" bg="rgba(255,255,255,0.72)" border="1.5px solid rgba(24,12,48,0.1)" fontWeight="500" fontSize="14px" minH="80px" _hover={{ borderColor: drgba("#7b2ff7", 0.4) }} _focusVisible={{ borderColor: "#7b2ff7", boxShadow: `0 0 0 3px ${drgba("#7b2ff7", 0.16)}` }} />
                      </Box>
                      <Box>
                        <Text fontSize="14px" fontWeight="700" color="#3f3a4d" mb="8px">Zapisy zamykają się (opcjonalnie)</Text>
                        <HStack spacing="9px" maxW="240px" bg="rgba(255,255,255,0.72)" border="1.5px solid rgba(24,12,48,0.1)" borderRadius="12px" px="12px" h="46px" _focusWithin={{ borderColor: "#7b2ff7", boxShadow: `0 0 0 3px ${drgba("#7b2ff7", 0.16)}` }}>
                          <Icon as={PiCalendarBlankDuotone} boxSize="17px" color="#7b2ff7" />
                          <Input type="date" variant="unstyled" value={signupClosesAt} onChange={e => setSignupClosesAt(e.target.value)} fontWeight="700" fontSize="14px" />
                        </HStack>
                      </Box>
                    </VStack>
                  </Collapse>
                </Section>
              </Box>
            </Box>
          </MotionDiv>

          <Box display={{ base: "block", xl: "none" }} borderRadius="20px" px="20px" py="20px" mt="24px" sx={GLASS}>{rail}</Box>

          <Box position="sticky" bottom="16px" zIndex={20} mt="28px">
            <Flex align="center" justify="space-between" gap="12px" borderRadius="18px" px={{ base: "14px", md: "16px" }} py="12px" direction={{ base: "column-reverse", sm: "row" }} sx={{ ...GLASS, background: "rgba(255,255,255,0.74)" }} boxShadow="inset 0 1px 0 rgba(255,255,255,0.75), 0 -6px 28px -18px rgba(43,0,90,0.4), 0 18px 44px -26px rgba(43,0,90,0.5)">
              <Button variant="ghost" color="#6b6480" leftIcon={<Icon as={PiCaretLeftBold} boxSize="13px" />} onClick={() => navigate("/organizator/grupy")} w={{ base: "100%", sm: "auto" }} _hover={{ bg: "rgba(24,12,48,0.05)" }}>Anuluj</Button>
              <MotionDiv whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }} style={{ flex: "0 0 auto" }}>
                <Button bg="accent" color="white" borderRadius="14px" fontWeight="800" h="52px" px="34px" fontSize="15px" onClick={create} isLoading={saving} loadingText="Tworzę…" isDisabled={!canCreate} boxShadow="0 14px 30px -12px rgba(229,31,68,0.6)" _hover={{ bg: "#c41a3a" }} _disabled={{ opacity: 0.5, cursor: "not-allowed", boxShadow: "none" }}>Utwórz grupę</Button>
              </MotionDiv>
            </Flex>
          </Box>
        </Box>
      </Box>

      <Box as="aside" display={{ base: "none", xl: "block" }} w="384px" flexShrink={0} alignSelf="stretch">
        <Box position="sticky" top="0" h="100vh" p="12px">
          <Box position="relative" h="100%" borderRadius="26px" overflow="hidden" sx={GLASS}>
            <Box position="absolute" inset={0} pointerEvents="none" sx={{ background: "radial-gradient(58% 40% at 50% 20%, rgba(16,185,129,0.12), transparent 72%)" }} />
            <VStack position="relative" align="stretch" spacing="0" h="100%" px="20px" py="24px" sx={{ ...SCROLL_FOG, maskImage: "linear-gradient(to bottom, #000 0, #000 calc(100% - 24px), transparent 100%)", WebkitMaskImage: "linear-gradient(to bottom, #000 0, #000 calc(100% - 24px), transparent 100%)" }}>
              {rail}
            </VStack>
          </Box>
        </Box>
      </Box>
    </Flex>
  );
};

export default CreateGroupViewV2;
